import React from 'react'
import { Card, Typography, Button, Divider, IconButton, Drawer, Box, useMediaQuery, useTheme } from '@mui/material'
import { J2Multiply } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import {
  BasicSystemResponseModel,
  Element,
  SmartMeterModalModelResponse,
  SystemConfigurationComponentsResponse,
  RetrofitBatteryModalResponse,
} from 'src/Libs/shop/models'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import FixFlexBasicSystemCard from './SystemConfigurationCard/FixFlexSystemConfiguration'
import BasicSystemConfigurationCard from './SystemConfigurationCard/BasicSystemConfigurationCard'
import MainComponentSystemConfigurationCard from './SystemConfigurationCard/MainComponentSystemConfigurationCard'
import AdditionalComponentSystemConfigurationCard from './SystemConfigurationCard/AdditionalComponentSystemConfigurationCard'
import PowerMeterSystemConfigurationCard from './SystemConfigurationCard/PowerMeterSystemConfigurationCard'
import RetrofitBatterySystemConfigurationCard from './SystemConfigurationCard/RetrofitBatterySystemConfigurationCard'
import { ProgressBarSteps } from '../../types'
import { currencyFormatter } from '../../utils/formatter'
import { getCardHeight } from '../../utils'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import SmartMeterSystemConfigurationCard from './SystemConfigurationCard/SmartMeterSystemConfigurationCard'
import { Component, ShippingDate } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationModel'
import QntmProductConfigurationCard from './SystemConfigurationCard/QntmProductConfigurationCard'
import WarrantyProductConfigurationCard from './SystemConfigurationCard/WarrantyProductConfigurationCard'

interface SystemConfigurationCardProps {
  showMobile: boolean
  progress: number
  onClose: () => void
  navigateTo: (page: ProgressBarSteps) => void
  basicData: BasicSystemResponseModel | null
  deliveryDate: ShippingDate | null
  selectedMainComponents: Component[] | null
  mainComponent: SystemConfigurationComponentsResponse | null
  selectedPowerMeters?: Element[]
  selectedWarrantyExtensionId?: string | null
  selectedBattery?: RetrofitBatteryModalResponse | null
  selectedSmartMeter?: SmartMeterModalModelResponse['elements'][number]
  totalPrice: number
  discountedPrice: number
  basicSystemImageUrl?: string
}

interface StyleProps {
  upLg: boolean
  cardHeight: number
  hasTabs: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    maxWidth: '100vw',
    marginTop: props.hasTabs ? theme.spacing(4) : 0,
    height: `calc(100vh - ${props.cardHeight}px)`,
    position: 'relative',
    padding: theme.spacing(2),
    paddingRight: 0,
    [theme.breakpoints.only('xs')]: {
      minHeight: '100%',
    },
    [theme.breakpoints.down('md')]: {
      height: 'calc(100vh - 176px)',
    },
  },

  cardWrapper: {
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 'calc(100% - 100px)',
    maxHeight: 'calc(100% - 100px)',
    paddingRight: theme.spacing(2),
  },
  exitButton: {
    color: theme.palette.grey.grey00,
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  spaceBetweenSpacing: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  divider: {
    margin: '20px auto',
    borderColor: theme.palette.background.paperBorder,
  },
  info: {
    color: theme.palette.shades.shade02,
  },
  drawer: {
    zIndex: 1400,
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    height: '68px',
    marginTop: '6px',
  },
}))

/*
 * Displays the system configuration summary card for desktop and mobile
 */
const SystemConfigurationCard: React.VFC<SystemConfigurationCardProps> = ({
  showMobile,
  progress,
  onClose,
  navigateTo,
  basicData,
  deliveryDate,
  mainComponent,
  selectedMainComponents,
  selectedPowerMeters,
  selectedWarrantyExtensionId,
  selectedBattery = null,
  selectedSmartMeter,
  totalPrice,
  discountedPrice,
  basicSystemImageUrl,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const upLg = useMediaQuery(theme.breakpoints.up('lg'))
  const { tabs } = useHeaderTabs()
  const hasTabs = tabs.length > 0
  const cardHeight = getCardHeight(upLg, hasTabs)
  const { classes } = useStyles({ upLg, cardHeight, hasTabs })

  const systemConfigurationDiscountPrice = currencyFormatter(discountedPrice)

  const content = (
    <>
      <Typography variant="h6">{t('shop.sytem-configure-card.title')}</Typography>

      <Box className={classes.cardWrapper}>
        <Box className={classes.spaceBetweenSpacing}>
          <Typography className={classes.info} variant="h5">
            {t('shop.system-configure-card.delivery-option')}
          </Typography>
          {progress > ProgressBarSteps.BasicSystem && (
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                navigateTo(ProgressBarSteps.BasicSystem)
              }}
            >
              {t('shop.system-configure-card.change')}
            </Button>
          )}
        </Box>
        <FixFlexBasicSystemCard basicData={basicData} deliveryDate={deliveryDate} />
        <Box className={classes.spaceBetweenSpacing}>
          <Typography className={classes.info} variant="h5">
            {t('shop.system-configure-card.basic-system')}
          </Typography>
        </Box>

        <BasicSystemConfigurationCard image={basicSystemImageUrl} name={basicData?.translated.name} />

        <Box className={classes.spaceBetweenSpacing}>
          <Typography variant="h5" className={classes.info}>
            {t('shop.system-configure-card.main-component')}
          </Typography>
          {progress > ProgressBarSteps.MainComponent && (
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                navigateTo(ProgressBarSteps.MainComponent)
              }}
            >
              {t('shop.system-configure-card.change')}
            </Button>
          )}
        </Box>

        {mainComponent?.em360Product && <QntmProductConfigurationCard product={mainComponent.em360Product} />}
        <MainComponentSystemConfigurationCard mainComponent={mainComponent} selectedMainComponents={selectedMainComponents} />
        <Divider className={classes.divider} />

        <Box className={classes.spaceBetweenSpacing}>
          <Typography variant="h5" className={classes.info}>
            {t('shop.system-configure-card.additional-component')}
          </Typography>
        </Box>
        <Box className={classes.spaceBetweenSpacing}>
          {mainComponent?.warrantyExtensions && selectedWarrantyExtensionId && (
            <WarrantyProductConfigurationCard products={mainComponent.warrantyExtensions} selectedWarrantyExtensionId={selectedWarrantyExtensionId} />
          )}
        </Box>

        <AdditionalComponentSystemConfigurationCard mainComponent={mainComponent} selectedMainComponents={selectedMainComponents} />
        {selectedPowerMeters && <PowerMeterSystemConfigurationCard powerMeters={selectedPowerMeters} />}
        <RetrofitBatterySystemConfigurationCard selectedRetrofitBattery={selectedBattery} />
        {selectedSmartMeter && <SmartMeterSystemConfigurationCard selectedSmartMeter={selectedSmartMeter} />}
      </Box>

      <Box className={classes.priceContainer}>
        {discountedPrice !== totalPrice && (
          <Typography
            variant="h2"
            color="inherit"
            sx={{
              textAlign: 'right',
              marginRight: '6px',
              textDecorationLine: 'line-through',
            }}
          >
            {`${t('shop.system-configure-card.rrp')}: ${currencyFormatter(totalPrice)}`}
          </Typography>
        )}

        <Typography variant="h2" color="primary" sx={{ textAlign: 'right', marginTop: '12px', marginRight: '6px' }}>
          {systemConfigurationDiscountPrice}
        </Typography>
      </Box>
    </>
  )

  return (
    <>
      <Box sx={{ display: isMobile ? 'none' : 'inherit' }}>
        <Card className={classes.container}>{content}</Card>
      </Box>

      <Box sx={{ display: isMobile ? 'inherit' : 'none' }}>
        <Drawer
          anchor="right"
          open={showMobile}
          className={classes.drawer}
          PaperProps={{
            sx: {
              backgroundColor: '#ffffff',
              borderRadius: 0,
              margin: 0,
              padding: 0,
              minWidth: '100%',
            },
          }}
        >
          <Card className={classes.container}>
            <IconButton className={classes.exitButton} size="large" onClick={() => onClose()}>
              <J2Multiply sx={{ width: '32px', height: '32px' }} />
            </IconButton>

            {content}
          </Card>
        </Drawer>
      </Box>
    </>
  )
}

export default SystemConfigurationCard
